/**
 * Wins of the Week — public marketing podium.
 *
 * Renders the top 3 wholesalers by # of WON deals in the last 7 days. Loads
 * from /api/public/leaderboard/wins-of-the-week which returns either the
 * Sunday-night snapshot (deterministic for the week) or a live-compute
 * fallback when no snapshot exists yet.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Trophy, MapPin, Loader2, ArrowUpRight, Sparkles, Share2 } from "lucide-react";

const MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };
const RANK_GRADIENT = {
  1: "linear-gradient(135deg, #F59E0B 0%, #FB923C 50%, #EF4444 100%)",
  2: "linear-gradient(135deg, #CBD5E1 0%, #94A3B8 100%)",
  3: "linear-gradient(135deg, #B45309 0%, #92400E 100%)",
};
const RANK_LABEL = { 1: "Gold", 2: "Silver", 3: "Bronze" };

function formatWeekEnding(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
  } catch { return iso; }
}

function PodiumCard({ row, onShare }) {
  const rank = row.rank;
  const gradient = RANK_GRADIENT[rank] || RANK_GRADIENT[3];
  return (
    <div
      data-testid={`podium-card-rank-${rank}`}
      className="rounded-2xl p-6 shadow-2xl text-white flex flex-col gap-3 relative overflow-hidden"
      style={{ background: gradient }}
    >
      <div className="absolute -top-4 -right-4 text-8xl opacity-20 select-none pointer-events-none">
        {MEDAL[rank]}
      </div>
      <div className="text-xs uppercase tracking-[0.18em] font-bold opacity-90">
        {RANK_LABEL[rank]} · Rank #{rank}
      </div>
      <div className="font-display font-extrabold text-2xl leading-tight">
        {row.display_name}
      </div>
      {row.company && (
        <div className="text-sm font-semibold opacity-95 -mt-1">{row.company}</div>
      )}
      {row.metro && (
        <div className="inline-flex items-center gap-1 text-xs font-medium opacity-90">
          <MapPin size={12} /> {row.metro}
        </div>
      )}
      <div className="mt-2 pt-3 border-t border-white/30 flex items-baseline gap-2">
        <span className="text-4xl font-black tabular-nums">{row.wins}</span>
        <span className="text-sm opacity-90">deal{row.wins === 1 ? "" : "s"} closed</span>
      </div>
      <button
        type="button"
        onClick={() => onShare(rank, row)}
        className="mt-3 inline-flex items-center justify-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white text-xs font-bold py-2 px-3 rounded-lg border border-white/20 transition-colors"
        data-testid={`podium-share-rank-${rank}`}
        title="Tweet your podium card"
      >
        <Share2 size={12} /> Share my card
      </button>
    </div>
  );
}

export default function WinsOfTheWeek() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referrer, setReferrer] = useState(null);
  const [searchParams] = useSearchParams();
  const focusRank = parseInt(searchParams.get("rank") || "0", 10);
  const refCode = (searchParams.get("ref") || "").toUpperCase();

  // Persist referral attribution the moment someone lands here from a tweet,
  // and resolve who the referrer is for the "invited by" pill.
  useEffect(() => {
    if (!refCode) return;
    try { localStorage.setItem("af_ref_code", refCode); } catch { /* private mode */ }
    axios.get(`${API}/public/referrals/${refCode}`)
      .then((r) => setReferrer(r.data))
      .catch(() => setReferrer(null));
  }, [refCode]);

  // Build a referral-aware register URL so the CTAs forward attribution.
  const registerHref = refCode ? `/register?ref=${refCode}` : "/register";

  useEffect(() => {
    // SEO meta tags.
    document.title = focusRank
      ? `${RANK_LABEL[focusRank] || "Top"} medal — Wins of the Week — iSpeedToClose`
      : "Wins of the Week — iSpeedToClose";
    const setMeta = (sel, attrs) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      }
    };
    setMeta('meta[name="description"]', {
      name: "description",
      content: "The top wholesalers closing deals on iSpeedToClose this week — updated every Sunday.",
    });
    const ogTitle = focusRank
      ? `${MEDAL[focusRank] || "🏆"} ${RANK_LABEL[focusRank] || "Top"} this week on iSpeedToClose`
      : "Wins of the Week — iSpeedToClose";
    setMeta('meta[property="og:title"]', { property: "og:title", content: ogTitle });
    setMeta('meta[property="og:description"]', {
      property: "og:description",
      content: "See who's closing the most deals this week. Speed is the deal.",
    });
    // Swap OG image to the per-rank solo card when ?rank=N is present.
    const ogImage = focusRank
      ? `${API}/wins-of-the-week/og/${focusRank}.png`
      : `${API}/wins-of-the-week/og.png`;
    setMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
    setMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    setMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    setMeta('meta[property="og:url"]', {
      property: "og:url", content: window.location.origin + window.location.pathname + window.location.search,
    });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: ogTitle });
    setMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: "See who's closing the most deals this week. Speed is the deal.",
    });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    axios.get(`${API}/public/leaderboard/wins-of-the-week`)
      .then((r) => setData(r.data))
      .catch(() => setData({ podium: [], is_live: true }))
      .finally(() => setLoading(false));
  }, [focusRank]);

  const handleShare = (rank, row) => {
    const medal = MEDAL[rank] || "🏆";
    const label = RANK_LABEL[rank] || "Top";
    const tweet = `${medal} Took ${label.toLowerCase()} on the @iSpeedToClose Wins of the Week leaderboard — ${row.wins} deal${row.wins === 1 ? "" : "s"} closed in 7 days. Speed is the deal.`;
    // Crawler-friendly share URL — Twitter fetches the backend HTML, parses
    // OG meta, and unfurls the per-rank solo PNG inline.
    const shareUrl = `${API}/share/wins-of-the-week?rank=${rank}`;
    const intent = `https://x.com/intent/tweet?text=${encodeURIComponent(tweet)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(intent, "_blank", "noopener,noreferrer");
  };

  const podium = data?.podium || [];
  // Reorder visually: 2nd / 1st / 3rd for the classic podium silhouette.
  const visualOrder = podium.length === 3
    ? [podium[1], podium[0], podium[2]]
    : podium;

  return (
    <div className="min-h-screen bg-[#0B132B] text-white" data-testid="wins-of-the-week-page">
      {/* Top bar */}
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-xl">
            <span className="w-7 h-7 rounded bg-emerald-500 flex items-center justify-center text-[#0B132B] text-sm font-black">i</span>
            iSpeedToClose
          </Link>
          <Link
            to={registerHref}
            className="text-xs uppercase tracking-wider font-bold bg-emerald-500 text-[#0B132B] px-4 py-2 rounded-full hover:bg-emerald-400 transition-colors"
            data-testid="wotw-cta-register"
          >
            Start free →
          </Link>
        </div>
        {referrer && (
          <div
            className="max-w-5xl mx-auto px-6 pb-3 -mt-1"
            data-testid="wotw-invited-by"
          >
            <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
              <Sparkles size={12} />
              <span>
                Invited by <strong className="text-white">{referrer.referrer_name}</strong>
                {referrer.referrer_company ? ` · ${referrer.referrer_company}` : ""}
                {" "}— sign up and you both get a free month.
              </span>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-14 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs uppercase tracking-[0.18em] font-bold mb-5">
          <Sparkles size={12} /> This Week&apos;s Winners
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mb-3 leading-[1.1]">
          Wins of the Week
        </h1>
        <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">
          The wholesalers who closed the most deals on iSpeedToClose this week.
          Snapshot taken every Sunday at 10 PM UTC.
        </p>
        {data?.week_ending && (
          <div className="mt-4 text-xs text-slate-400 tabular-nums">
            Week ending {formatWeekEnding(data.week_ending)}
            {data.is_live && (
              <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] uppercase tracking-wider">live</span>
            )}
          </div>
        )}
      </section>

      {/* Podium */}
      <section className="max-w-5xl mx-auto px-6 pb-14">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="animate-spin mr-2" size={18} /> Loading podium…
          </div>
        ) : podium.length === 0 ? (
          <div
            data-testid="wotw-empty-state"
            className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center"
          >
            <Trophy className="mx-auto mb-3 text-amber-400" size={36} />
            <div className="font-display font-bold text-xl mb-2">The podium is open</div>
            <div className="text-slate-300 max-w-md mx-auto text-sm">
              No deals closed by opted-in wholesalers this week yet — but the
              week&apos;s not over. Sign up, close a deal, and your name lands here Sunday night.
            </div>
            <Link
              to={registerHref}
              className="mt-5 inline-flex items-center gap-2 bg-emerald-500 text-[#0B132B] font-bold text-sm px-5 py-2.5 rounded-full hover:bg-emerald-400"
            >
              Claim your spot <ArrowUpRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 items-end">
            {visualOrder.map((row, idx) => (
              <div
                key={row.user_id || idx}
                className={
                  // Tallest in the middle (1st), then 2nd, then 3rd.
                  podium.length === 3
                    ? idx === 1 ? "sm:order-2 sm:scale-105 sm:-mt-4"
                    : idx === 0 ? "sm:order-1" : "sm:order-3"
                    : ""
                }
              >
                <PodiumCard row={row} onShare={handleShare} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA strip */}
      <section className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-10 sm:flex sm:items-center sm:justify-between gap-6">
          <div>
            <div className="font-display font-bold text-xl mb-1">Want your name here next week?</div>
            <div className="text-sm text-slate-300">
              Opt in to the public leaderboard from <span className="text-emerald-400 font-semibold">Settings</span> after you sign up. The more deals you close, the higher you climb.
            </div>
          </div>
          <Link
            to={registerHref}
            className="mt-4 sm:mt-0 inline-flex items-center gap-2 bg-emerald-500 text-[#0B132B] font-bold text-sm px-6 py-3 rounded-full hover:bg-emerald-400 whitespace-nowrap"
            data-testid="wotw-cta-bottom"
          >
            Start free — 14-day Pro trial <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
